import moment from 'moment-timezone';
import React from 'react';
import { isDeliveryTimeInPast } from 'src/app/Orders/order.helpers';
import checkCircleIcon from 'src/assets/icons/check-circle-icon.svg';
import { ORDER_STATUS } from 'src/constants/common';
import { convertUtcToUserTimezone, getDateWithSuffixFormat } from 'src/utils/CommonFunctions';

import { ITrackOrder } from './track.slice';
import { checkDateFormat, checkLocationUpdate, getDeliveryDateObject, getDotColor, getLastLocationUpdateObject, getPickupDateObject, getToFormatWiseDateTime } from './track.utils';

export interface ITrackStatusStrip {
    icon: React.ReactNode | null;
    dotColor: 'primary' | 'red' | 'yellow' | 'green' | undefined;
    label: React.ReactNode;
    hasContent: boolean;
}

export const getTrackStatusStrip = (order: ITrackOrder, timezone: string, t: (key: string, opts?: any) => string): ITrackStatusStrip => {
    const orderStatus = {
        isPickup: order?.orderStatus === ORDER_STATUS.BOOKED || order?.orderStatus === ORDER_STATUS.DISPATCHED,
        isDelivered: order?.orderStatus === ORDER_STATUS.DELIVERED,
        isInTransit: order?.orderStatus === ORDER_STATUS.IN_TRANSIT,
    };

    const scheduleObj = order?.schedule;
    const subStatus = order?.radarSubStatus;

    const buildDotLabel = (): React.ReactNode => {
        const pickupDateObj = getPickupDateObject(scheduleObj, orderStatus.isPickup);
        const deliveryDateObj = getDeliveryDateObject(scheduleObj, orderStatus.isDelivered);
        const lastLocationObj = getLastLocationUpdateObject(order);

        return (
            <>
                <span>{(orderStatus.isPickup ? t('Radar.Picking_Up_Status') : orderStatus.isDelivered ? t('Radar.Estimated_delivery') : t('Radar.Last_location_update')) as string}</span>
                {(orderStatus.isPickup || orderStatus.isDelivered) && (
                    <span
                        className="ml-1"
                        dangerouslySetInnerHTML={{
                            __html: getToFormatWiseDateTime(orderStatus.isPickup ? pickupDateObj : deliveryDateObj, timezone),
                        }}
                    />
                )}
                {orderStatus.isInTransit && (
                    <span
                        className="ml-1"
                        dangerouslySetInnerHTML={{
                            __html: checkLocationUpdate(lastLocationObj?.startDateTime, timezone),
                        }}
                    />
                )}
            </>
        );
    };

    if (subStatus === 'completed') {
        const isPickupSide = orderStatus.isPickup || orderStatus.isInTransit;
        const actualTime = isPickupSide ? scheduleObj?.actualPickupDatetime : scheduleObj?.actualDeliveryDatetime;

        if (actualTime) {
            // 'Do' + getDateWithSuffixFormat so the day's ordinal renders as a <sup>, matching the
            // Overview header and the detail page's schedule cards. The label is interpolated into an
            // i18n string that is already rendered via dangerouslySetInnerHTML (escapeValue is off).
            const formattedTime = getDateWithSuffixFormat(`${convertUtcToUserTimezone(actualTime, 'MMM Do, YYYY [at] h:mm A', true, timezone)}`);

            return {
                icon: <img src={checkCircleIcon} alt="check" className="w-4 h-4 flex-none" />,
                dotColor: undefined,
                label: <span dangerouslySetInnerHTML={{ __html: isPickupSide ? t('Radar.Picked_Up_On_Time', { time: formattedTime }) : t('Radar.Delivered_On_Time', { time: formattedTime }) }} />,
                hasContent: true,
            };
        }
    }

    if (subStatus === 'delayed') {
        const deliveryDateObj = getDeliveryDateObject(scheduleObj, true);
        const deliveryStart = deliveryDateObj?.startDateTime;
        const dueDate = deliveryStart ? checkDateFormat(deliveryStart, timezone, ['dddd, MMM Do', 'dddd, MMM Do']) : '';
        // Delayed also covers a shipment running behind pace whose delivery is still ahead, so
        // "was due" only fits once the delivery time has passed. Same deadline as the BE rule:
        // start date + start time, 09:00 when unset, in the consignee's zone.
        const isDeliveryTimePassed = deliveryStart
            ? isDeliveryTimeInPast(`${moment.utc(deliveryStart).format('YYYY-MM-DD')} ${deliveryDateObj?.startTime || '09:00:00'}`, scheduleObj?.deliveryTimezone || timezone)
            : false;

        if (deliveryStart && !isDeliveryTimePassed) {
            const formatTime = (time: string) => moment(time, 'HH:mm:ss').format('h:mmA');
            const timeRange = deliveryDateObj?.startTime
                ? deliveryDateObj.hasWindow
                    ? `${formatTime(deliveryDateObj.startTime)} - ${formatTime(deliveryDateObj.endTime)}`
                    : formatTime(deliveryDateObj.startTime)
                : '';

            return {
                icon: null,
                dotColor: 'red',
                label: <span dangerouslySetInnerHTML={{ __html: t('Radar.Delivery_Likely_Late', { dueDate: `${dueDate}${timeRange ? ` ${timeRange}` : ''}` }) }} />,
                hasContent: true,
            };
        }

        return {
            icon: null,
            dotColor: 'red',
            label: <span dangerouslySetInnerHTML={{ __html: t('Radar.Delivery_Was_Due', { dueDate }) }} />,
            hasContent: true,
        };
    }

    if (subStatus === 'require_update') {
        return {
            icon: null,
            dotColor: 'yellow',
            label: buildDotLabel(),
            hasContent: true,
        };
    }

    return {
        icon: null,
        dotColor: getDotColor(order),
        label: buildDotLabel(),
        hasContent: true,
    };
};
